export type ScrollEdgeFadeStop = readonly [fraction: number, alpha: number];
/** At least two stops, so a gradient's colour/location tuples are total. */
export type ScrollEdgeFadeRamp = readonly [
  ScrollEdgeFadeStop,
  ScrollEdgeFadeStop,
  ...ScrollEdgeFadeStop[],
];

/**
 * The dissolve, as `[fractionOfBand, alpha]` pairs read from the band's inner side to the edge it
 * hugs. `alpha` is how far the content has dissolved: an overlay paints the surface colour at that
 * alpha, and a mask keeps `1 - alpha` of the content.
 *
 * The curve is deliberately EASED rather than linear. A linear alpha ramp over a 40pt band has a
 * visible onset: it is already ~25% opaque a quarter of the way in, which reads as a flat film
 * laid over otherwise-sharp content — the "shade" impression. Keeping alpha near zero for the
 * first third and pushing most of the opacity into the last third makes the band start invisibly
 * and finish completely, so content reads as thinning out.
 *
 * The ramp dissolves the content completely SHORT of the edge (the repeated `1` below), so the
 * outermost sliver shows none of it: content that is 90% dissolved at the very last pixel still
 * reads as clipped, which is the exact impression the band exists to avoid.
 */
export const SCROLL_EDGE_FADE_RAMP: ScrollEdgeFadeRamp = [
  [0, 0],
  [0.2, 0.05],
  [0.4, 0.2],
  [0.6, 0.45],
  [0.78, 0.75],
  [0.92, 1],
  [1, 1],
];

/**
 * The gradient axis, from the band's inner (content) side to the edge it hugs. Drawing along this
 * axis lets every edge share `SCROLL_EDGE_FADE_RAMP` as-is instead of mirroring its stops.
 */
export const SCROLL_EDGE_FADE_GRADIENT_AXIS = {
  bottom: { end: { x: 0.5, y: 1 }, start: { x: 0.5, y: 0 } },
  left: { end: { x: 0, y: 0.5 }, start: { x: 1, y: 0.5 } },
  right: { end: { x: 1, y: 0.5 }, start: { x: 0, y: 0.5 } },
  top: { end: { x: 0.5, y: 0 }, start: { x: 0.5, y: 1 } },
} as const;
